import React, { useEffect } from 'react';
import Button, { LinkButton } from '@atlaskit/button/new';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import { Anchor, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { useFlagService } from '@atlassian/jira-flags';
import { useGetJsmPremiumBillingUrl } from '@atlassian/jira-servicedesk-standard-to-premium-modal/src/services/use-get-jsm-premium-billing-url/index.tsx';
import { messages } from './messages.tsx';
import type { DirectBuyButtonProps } from './types.tsx';

// This button is only used for CCP customers
export const DirectBuyButton = ({
	analyticsOrigin,
	appearance,
	spacing,
	testId,
}: DirectBuyButtonProps) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();

	const handleOnClick = (
		_: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (analyticsOrigin) {
			fireUIAnalytics(analyticsEvent, 'jsmDirectBuyButton', {
				origin: analyticsOrigin,
			});
		}
	};

	const {
		data: { billingPageUrl },
		error,
	} = useGetJsmPremiumBillingUrl(true);

	useEffect(() => {
		if (error) {
			showFlag({
				type: 'error',
				isAutoDismiss: true,
				appearance: 'error',
				title: formatMessage(messages.errorFlagTitle),
				description: formatMessage(messages.errorFlagDescription),
				messageId: 'direct-buy-buttons.ui.show-flag.error',
				messageType: 'transactional',
			});
		}
	}, [error, formatMessage, showFlag]);

	if (appearance === 'subtle') {
		return billingPageUrl ? (
			<Anchor
				href={billingPageUrl}
				xcss={[anchorStyles, spacing === 'compact' && compactAnchorStyles]}
				target="_blank"
				testId={testId}
			>
				<EditionsIcon label="" color={token('color.icon.inverse')} />
				{formatMessage(messages.upgradeNow)}
			</Anchor>
		) : (
			<Anchor
				href="#"
				xcss={[anchorStyles, spacing === 'compact' && compactAnchorStyles, disabledAnchorStyles]}
				aria-disabled="true"
				onClick={(e) => e.preventDefault()}
			>
				<Spinner testId="direct-buy-buttons.ui.button-loading-spinner" appearance="invert" />
			</Anchor>
		);
	}

	if (appearance === 'warning') {
		return billingPageUrl ? (
			<LinkButton
				appearance="warning"
				href={billingPageUrl}
				iconBefore={EditionsIcon}
				spacing={spacing}
				target="_blank"
				testId={testId}
			>
				{formatMessage(messages.upgradeNow)}
			</LinkButton>
		) : (
			<Button
				appearance="warning"
				isLoading
				iconBefore={EditionsIcon}
				spacing={spacing}
				testId="direct-buy-buttons.ui.warning-button-loading"
			>
				{formatMessage(messages.upgradeNow)}
			</Button>
		);
	}

	return billingPageUrl ? (
		<LinkButton
			appearance="discovery"
			onClick={handleOnClick}
			href={billingPageUrl}
			iconBefore={EditionsIcon}
			spacing={spacing}
			target="_blank"
			testId={testId}
		>
			{formatMessage(messages.upgradeNow)}
		</LinkButton>
	) : (
		<Button
			appearance="discovery"
			isLoading
			iconBefore={EditionsIcon}
			spacing={spacing}
			testId="direct-buy-buttons.ui.default-button-loading"
		>
			{formatMessage(messages.upgradeNow)}
		</Button>
	);
};

const anchorStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	textDecoration: 'none !important',
	display: 'inline-flex',
	borderRadius: 'border.radius.100',
	flexShrink: 0,
	height: '2rem',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	paddingBlock: 'space.075',
	paddingInlineEnd: 'space.150',
	paddingInlineStart: 'space.150',
	textAlign: 'center',
	transition: 'background 0.1s ease-out',
	alignItems: 'center',
	color: 'color.text.inverse',
	gap: 'space.075',
	':hover': {
		backgroundColor: 'color.background.accent.gray.bolder.hovered',
	},
	':active': {
		backgroundColor: 'color.background.accent.gray.bolder.pressed',
	},
});

const compactAnchorStyles = xcss({
	height: '1.5rem',
	paddingBlock: 'space.025',
});

const disabledAnchorStyles = xcss({
	cursor: 'wait',
});
